/**
 * Shared billing utilities
 * Extracted from POSView and InvoicesView to avoid duplication
 */

/**
 * Format date string to DD/MM/YYYY (Indian standard)
 * @param {string|Date} dateStr - ISO date string or Date object
 * @returns {string} Formatted date or '-' if invalid
 */
export function formatDate(dateStr) {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '-'
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

/**
 * Format date+time string to DD/MM/YYYY HH:mm
 * @param {string|Date} dateStr - ISO date string or Date object
 * @returns {string} Formatted datetime or '-' if invalid
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '-'
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const mins = String(d.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${mins}`
}

/**
 * Payment mode options used across POS and Invoice forms
 */
export const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash', icon: 'cash', key: 'C' },
  { value: 'upi', label: 'UPI', icon: 'upi', key: 'U' },
]

/**
 * Sale type options (cash bill vs credit/udhar)
 */
export const SALE_TYPES = [
  { value: 'cash', label: 'Cash Bill' },
  { value: 'credit', label: 'Credit (Udhar)' },
]

/**
 * Payment mode options for Invoice Form (includes cheque)
 */
export const INVOICE_PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
]

const LAST_PAYMENT_MODE_KEY = 'billing_last_payment_mode'

/**
 * Get last used payment mode from localStorage
 * @returns {string} Payment mode value (defaults to 'cash')
 */
export function getLastPaymentMode() {
  try {
    return localStorage.getItem(LAST_PAYMENT_MODE_KEY) || 'cash'
  } catch {
    return 'cash'
  }
}

/**
 * Save last used payment mode to localStorage
 * @param {string} mode - Payment mode value
 */
export function saveLastPaymentMode(mode) {
  try {
    localStorage.setItem(LAST_PAYMENT_MODE_KEY, mode)
  } catch {
    // localStorage not available
  }
}

/**
 * Flatten nested print data from API into flat keys for TaxInvoicePrint/CashMemoPrint.
 * API returns: { invoice: {...}, business: {...}, customer: {...}, items: [...] }
 * Templates expect: { business_name, business_gstin, invoice_number, customer_name, items, ... }
 */
export function flattenPrintData(data) {
  const inv = data.invoice || {}
  const biz = data.business || {}
  const cust = data.customer || {}
  return {
    // Business fields
    business_name: biz.name,
    business_address: biz.address,
    business_gstin: biz.gstin,
    business_state: biz.state_code,
    business_phone: biz.phone,
    business_email: biz.email,
    bank_name: biz.bank_name,
    bank_account_no: biz.bank_account_no,
    bank_branch: biz.bank_branch,
    bank_ifsc: biz.bank_ifsc,
    bank_accounts: data.bank_accounts || [],
    // Customer fields
    customer_name: cust.name,
    customer_address: cust.address,
    customer_gstin: cust.gstin,
    customer_state_code: cust.state_code,
    customer_phone: cust.phone,
    customer_email: cust.email,
    // Invoice fields (spread all: invoice_number, invoice_type, invoice_date, payment_mode, cgst_amount, etc.)
    ...inv,
    // Items from print data — map localized_product_name for the template
    items: (data.items || inv.items || []).map((item) => ({
      ...item,
      product_name_tamil: item.localized_product_name || item.product_name_tamil,
    })),
  }
}
